import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../../../lib/api";
import { isSessionExpired } from "../../../lib/api";
import { isTerminalStatus } from "../../../lib/importStatus";
export type ImportProgress = {
  job_id: string;
  status: string;
  counts: Record<string, number>;
  commit_cursor: { next_row_index?: number };
  in_progress: boolean;
  heartbeat_at: string | null;
  cancel_requested_at: string | null;
};

const FAST_MS = 2000;
const MAX_MS = 8000;

function nextDelay(cur: number, changed: boolean): number {
  if (changed) return FAST_MS;
  return Math.min(MAX_MS, Math.max(FAST_MS, Math.round(cur * 1.5)));
}

function signatureOf(p: ImportProgress | null): string {
  if (!p) return "";
  return [
    p.status,
    p.heartbeat_at ?? "",
    ...Object.entries(p.counts || {}).map(([k, v]) => `${k}:${v}`),
    p.commit_cursor?.next_row_index ?? 0,
    p.cancel_requested_at ?? "",
  ].join("|");
}

/**
 * Shared progress-polling hook.
 *
 * - Polls only while status is COMMITTING or CANCELLING.
 * - Initial cadence 2s; bounded backoff to 8s when no counter/heartbeat/
 *   status change is observed.
 * - Resets to 2s on any signature change.
 * - Pauses when the document is hidden; on visibility restore, immediately
 *   refetches BOTH `job` (via `onRefetchJob`) and `progress`.
 * - Stops on terminal status.
 * - Two consecutive failures set `reconnecting=true`; suppresses toast spam
 *   and defers to the shared session-expiry singleton.
 */
export function useImportProgress(
  jobId: string,
  status: string,
  onRefetchJob: () => void,
): {
  progress: ImportProgress | null;
  reconnecting: boolean;
  poll: () => Promise<void>;
} {
  const [progress, setProgress] = useState<ImportProgress | null>(null);
  const [reconnecting, setReconnecting] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const delayRef = useRef<number>(FAST_MS);
  const sigRef = useRef<string>("");
  const failsRef = useRef<number>(0);
  const mountedRef = useRef<boolean>(true);
  const jobIdRef = useRef<string>(jobId);
  jobIdRef.current = jobId;

  // A monotonic run id — every remount / job change / poll-off / terminal
  // transition increments it. In-flight responses tagged with an older run
  // id are ignored so late-arriving stale replies cannot overwrite fresher
  // state or reschedule a dead timer.
  const runIdRef = useRef<number>(0);
  const pollingActiveRef = useRef<boolean>(false);

  const shouldPoll = status === "COMMITTING" || status === "CANCELLING";

  const doOne = useCallback(async () => {
    if (!mountedRef.current) return;
    const myRun = runIdRef.current;
    const myJob = jobIdRef.current;
    try {
      const { data } = await api.get<ImportProgress>(`/import/jobs/${myJob}/progress`);
      // Discard the response entirely if:
      //  - the component unmounted,
      //  - the run id was bumped (polling turned off, or job id changed,
      //    or a newer request already completed and moved the counter),
      //  - the job id changed underneath us.
      if (!mountedRef.current) return;
      if (myRun !== runIdRef.current) return;
      if (myJob !== jobIdRef.current) return;
      const sig = signatureOf(data);
      const changed = sig !== sigRef.current;
      sigRef.current = sig;
      setProgress(data);
      setReconnecting(false);
      failsRef.current = 0;
      if (isTerminalStatus(data.status)) {
        // Freeze this run so no more timers get scheduled and no stale
        // response can flip state.
        runIdRef.current += 1;
        pollingActiveRef.current = false;
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = null;
        onRefetchJob();
        return;
      }
      delayRef.current = nextDelay(delayRef.current, changed);
    } catch (e: unknown) {
      if (isSessionExpired(e)) return;
      if (!mountedRef.current) return;
      if (myRun !== runIdRef.current) return;
      failsRef.current += 1;
      if (failsRef.current >= 2) setReconnecting(true);
    }
  }, [onRefetchJob]);

  const scheduleNext = useCallback(() => {
    if (!mountedRef.current) return;
    if (!pollingActiveRef.current) return;
    if (document.visibilityState === "hidden") return;
    const myRun = runIdRef.current;
    timerRef.current = setTimeout(async () => {
      // If the run was bumped while we waited, do not fire another request
      // and do not reschedule.
      if (!mountedRef.current || myRun !== runIdRef.current || !pollingActiveRef.current) return;
      await doOne();
      scheduleNext();
    }, delayRef.current);
  }, [doOne]);

  // Kick off polling on entry to COMMITTING/CANCELLING and re-arm on
  // job id changes. Any prior in-flight request is invalidated via
  // runIdRef so its late response can't leak into the new run.
  useEffect(() => {
    mountedRef.current = true;
    // Invalidate any prior in-flight cycle unconditionally.
    runIdRef.current += 1;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    if (!shouldPoll) {
      pollingActiveRef.current = false;
      return;
    }
    pollingActiveRef.current = true;
    delayRef.current = FAST_MS;
    sigRef.current = "";
    failsRef.current = 0;
    doOne().then(scheduleNext);
    return () => {
      // Component unmount OR effect re-run: bump the run id and clear.
      runIdRef.current += 1;
      pollingActiveRef.current = false;
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = null;
    };
  }, [shouldPoll, jobId, doOne, scheduleNext]);

  // Visibility handling: pause when hidden, immediate refetch on show.
  useEffect(() => {
    const onVis = () => {
      if (!mountedRef.current || !pollingActiveRef.current) return;
      if (document.visibilityState === "hidden") {
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = null;
        return;
      }
      onRefetchJob();
      delayRef.current = FAST_MS;
      doOne().then(scheduleNext);
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [doOne, scheduleNext, onRefetchJob]);

  useEffect(() => () => {
    mountedRef.current = false;
    runIdRef.current += 1;
    pollingActiveRef.current = false;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
  }, []);

  return { progress, reconnecting, poll: doOne };
}
